import type { Messages } from "./en";

export const fr: Messages = {
  "nav.home": "Accueil LEARNABLE",
  "nav.main": "Principal",
  "nav.courses": "Cours",
  "nav.review": "Révision",
  "nav.progress": "Progrès",
  "nav.guide": "Guide",
  "nav.marketplace": "Marché",
  "nav.streak": "Série quotidienne : {n}",
  "nav.xp": "Expérience totale : {n} XP",

  "account.menu": "Compte",
  "account.settings": "Paramètres",
  "account.guide": "Comment fonctionne LEARNABLE",
  "account.signOut": "Se déconnecter",

  "common.save": "Enregistrer",
  "common.saved": "Enregistré.",
  "common.saving": "Enregistrement…",
  "common.cancel": "Annuler",
  "common.close": "Fermer",
  "common.pleaseWait": "Patientez…",

  "signin.title": "Connexion à LEARNABLE",
  "signin.registerTitle": "Créez votre compte",
  "signin.email": "E-mail",
  "signin.password": "Mot de passe",
  "signin.passwordRule": "Au moins 12 caractères.",
  "signin.submit": "Se connecter",
  "signin.register": "Créer le compte",
  "signin.forgot": "Mot de passe oublié ?",
  "signin.toRegister": "Créer un compte",
  "signin.toSignIn": "J'ai déjà un compte",
  "signin.language": "Langue",

  "password.current": "Mot de passe actuel",
  "password.new": "Nouveau mot de passe (12 caractères ou plus)",
  "password.change": "Changer le mot de passe",
  "password.changed": "Mot de passe changé. Vos autres appareils ont été déconnectés.",

  "settings.title": "Paramètres",
  "settings.intro": "Votre profil et vos préférences. Ils sont enregistrés dans votre compte et s'appliquent sur tous vos appareils.",
  "settings.profile": "Profil",
  "settings.displayName": "Nom affiché",
  "signin.emailOrUsername": "E-mail ou nom d'utilisateur",
  "signin.username": "Nom d'utilisateur (facultatif)",
  "signin.usernameHint": "3 à 30 lettres, chiffres, points, tirets ou underscores. Tu peux te connecter avec lui à la place de l'e-mail.",
  "settings.username": "Nom d'utilisateur",
  "settings.usernameHint": "Facultatif. Te permet de te connecter avec ce nom au lieu de l'e-mail. Les autres ne le voient pas. Laisse vide pour le retirer.",
  "settings.displayNameHint": "Affiché à la place de votre e-mail. Laissez vide pour utiliser votre e-mail.",
  "settings.email": "E-mail",
  "settings.emailHint": "L'adresse avec laquelle vous vous connectez.",
  "settings.language": "Langue",
  "settings.languageHint": "La langue des menus et des pages. Le contenu de vos cours et les commentaires de l'IA restent dans la langue du cours.",
  "settings.translationNote": "Traduction en cours : certains écrans sont encore en anglais.",
  "settings.study": "Étude",
  "settings.timezone": "Fuseau horaire",
  "settings.timezoneHint": "Votre journée d'étude, votre série et votre objectif quotidien suivent ce fuseau horaire.",
  "settings.dailyGoal": "Objectif quotidien",
  "settings.dailyGoalHint": "Réponses par jour.",
  "settings.security": "Sécurité",
  "settings.signOutHint": "La déconnexion efface les données de votre compte de ce navigateur.",

  "guide.title": "Comment fonctionne LEARNABLE",
  "guide.intro": "LEARNABLE transforme vos propres supports de cours en questions, vérifie vos réponses et planifie chaque révision juste avant que vous n'oubliiez.",
  "guide.contents": "Sommaire",
  "guide.structure.title": "1. L'organisation d'un cours",
  "guide.structure.body": "Un cours est divisé en chapitres, les chapitres en thèmes et les thèmes en concepts. Chaque concept contient des éléments d'apprentissage : les faits à retenir, chacun posé sous la forme d'une ou plusieurs questions.",
  "guide.material.title": "2. Ajoutez vos supports",
  "guide.material.body": "Ouvrez un cours et importez des fichiers PDF, Word, PowerPoint, texte ou Markdown, ou collez du texte. Si vous avez déjà des questions et réponses, importez-les avec des lignes commençant par \"Question:\" et \"Answer:\" (ou \"Domanda:\" et \"Risposta:\") : chacune devient un concept, sans IA.",
  "guide.curriculum.title": "3. Construisez le programme",
  "guide.curriculum.body": "Demandez à l'IA de proposer des chapitres, thèmes et concepts à partir de vos supports. Relisez la proposition, renommez, déplacez ou supprimez ce que vous voulez, puis acceptez-la. Rien ne change tant que vous n'avez pas accepté.",
  "guide.study.title": "4. Étudiez un concept",
  "guide.study.body": "Ouvre un concept et appuie sur Activer : ses éléments d'apprentissage sont préparés à partir de ton matériel. Lis-les, puis appuie sur « J'ai étudié ce concept ». Chaque question est posée jusqu'à ce que tu y répondes bien trois fois (pas forcément de suite), avec un retour après chaque réponse.",
  "guide.answer.title": "5. Répondez et recevez un retour",
  "guide.answer.body": "Écrivez votre réponse avec vos propres mots, ou appuyez sur \"Dites votre réponse\" pour la dicter. Un indice est disponible, mais il divise les XP par deux. L'IA juge le sens, pas les mots exacts, et affiche quatre scores et une liste de ce qui était juste, manquant ou faux. La réponse de référence et ses sources sont toujours à un clic.",
  "guide.disagree.title": "6. Si vous n'êtes pas d'accord",
  "guide.disagree.body": "Appuyez sur \"Disagree with the grade?\". Vous pouvez expliquer pourquoi et demander un second avis à l'IA (les deux restent visibles), ou noter vous-même la réponse. Votre note l'emporte toujours.",
  "guide.review.title": "7. Révisez au bon moment",
  "guide.review.body": "Chaque réponse place sa question dans un calendrier : mieux vous la connaissez, plus elle revient tard. La page Révision et votre tableau de bord indiquent ce qui est à réviser et le temps nécessaire approximatif.",
  "guide.progress.title": "8. Suivez vos progrès",
  "guide.progress.body": "Votre série compte les jours où vous avez étudié, les XP récompensent les bonnes réponses et l'objectif quotidien fixe votre rythme. \"Ce que vous ratez encore\" rassemble les erreurs trouvées dans vos réponses récentes, avec un bouton pour vous entraîner.",
  "guide.organise.title": "9. Gardez tout en ordre",
  "guide.organise.body": "Dans \"Gérer les questions\", vous pouvez sélectionner des questions isolées ou des chapitres, thèmes et concepts entiers pour les supprimer, les mettre en pause, les reprendre ou les déplacer, modifier une question et faire glisser ⠿ pour réordonner. Les longues sections se replient avec la flèche à côté de leur titre.",
  "guide.marketplace.title": "10. Partagez et obtenez des cours",
  "guide.marketplace.body": "Dans le Marketplace, vous trouvez des cours créés par d'autres utilisateurs, par catégorie, niveau, langue et taille. Avant d'en ajouter un, vous voyez sa page : ce qu'il couvre, à qui il s'adresse, ce que vous apprendrez, ses chapitres et son nombre de questions. \"Ajouter à mes cours\" le place sur votre tableau de bord : son auteur tient les questions à jour et vous ne pouvez pas les modifier, mais votre étude vous appartient (votre plan, vos réponses, vos XP et la priorité que vous donnez à chaque question). Le matériel de l'auteur ne peut pas être téléchargé. Pour partager un de vos cours, ouvrez-le et rédigez sa \"Page du marketplace\", enregistrez-la comme brouillon, puis publiez-la.",
  "guide.glossary.title": "Les mots de l'application",
  "guide.term.concept": "Concept",
  "guide.term.concept.def": "Une idée de votre cours, comme une définition ou une règle.",
  "guide.term.item": "Élément d'apprentissage",
  "guide.term.item.def": "Un fait à retenir dans un concept, avec ses questions et sa réponse attendue.",
  "guide.term.active": "Active (actif)",
  "guide.term.active.def": "Le concept participe à l'étude et aux révisions. Les concepts en pause ou non étudiés, non.",
  "guide.term.grades": "À revoir · Difficile · Bien · Facile",
  "guide.term.grades.def": "À revoir, difficile, bien, facile : à quel point vous connaissiez la réponse. Cela décide quand la question revient.",
  "guide.term.mastery": "Maîtrise",
  "guide.term.mastery.def": "Une estimation de votre mémoire d'un concept, d'après vos révisions.",
  "guide.term.xp": "XP et série",
  "guide.term.xp.def": "Des points pour les bonnes réponses, et le nombre de jours d'étude d'affilée.",
  "guide.privacy.title": "Vos données",
  "guide.privacy.body": "Vos cours, réponses et progrès ne sont visibles que par vous. La dictée est transcrite par votre navigateur ; seul le texte est conservé.",
  "help.button": "Aide : {topic}",
  "help.more": "En savoir plus",
  "help.nextStep": "La chose la plus utile à faire maintenant. Les révisions à faire passent toujours en premier, puis les nouveautés.",
  "help.courses": "Une carte par cours. Le bouton vert révise ce qui est à faire ; le bleu continue avec quelque chose de nouveau.",
  "help.streak": "Jours d'affilée avec au moins une réponse. Répondez à une question aujourd'hui pour la garder.",
  "help.xp": "Points d'expérience, gagnés grâce aux bonnes réponses. Un indice les divise par deux.",
  "help.goal": "Le nombre de réponses que vous voulez donner chaque jour. Modifiable dans les Paramètres.",
  "help.planner": "Combien de questions arrivent à échéance à chaque moment, et le temps approximatif nécessaire.",
  "help.coursePlanner": "Ce cours seulement : combien de ses questions arrivent à échéance et quand, et combien de temps elles prennent environ. Le nouveau matériel demande au moins trois bonnes réponses par question.",
  "help.activity": "Un carré par jour : plus il est foncé, plus vous avez répondu.",
  "help.concept": "Un concept est une idée de votre cours. Commencez à l'étudier pour préparer ses questions, lisez ses éléments d'apprentissage, puis entraînez-vous.",
  "help.consolidate": "Appuie sur le bouton quand tu as lu les éléments d'apprentissage ci-dessous. Chaque question est posée jusqu'à ce que tu y répondes bien trois fois, pour que ça reste.",
  "help.study": "Écrivez la réponse avec vos mots ou dictez-la. L'IA juge le sens, pas les mots exacts, et montre ce qui était juste ou manquant.",
  "help.questions": "Cochez des questions isolées ou un chapitre, thème ou concept entier, puis supprimez, mettez en pause ou déplacez-les. Faites glisser ⠿ pour changer l'ordre.",
  "help.marketplace": "Des cours créés par d'autres utilisateurs. Ouvrez-en un pour voir ce qu'il couvre et combien de questions il contient ; ajoutez-le pour l'étudier avec votre propre plan. Son auteur le tient à jour.",
  "help.weakSpots": "Les erreurs trouvées dans vos dernières réponses, regroupées par concept. Entraînez-vous jusqu'à ce qu'elles disparaissent de la liste.",
  "help.material": "Importez des fichiers PDF, Word, PowerPoint ou texte, ou collez du texte. LEARNABLE en tire le cours et ses questions.",
  "start.title": "Comment fonctionne LEARNABLE",
  "start.intro": "Quatre étapes. Ouvrez-en une pour la voir dans le guide.",
  "start.hide": "Compris, masquer",
  "start.reopen": "Voir comment fonctionne LEARNABLE",
  "start.material.title": "Ajoutez vos supports",
  "start.material.body": "Créez un cours et importez vos notes, diapositives ou livres, ou collez du texte.",
  "start.plan.title": "Validez le programme",
  "start.plan.body": "L'IA propose des chapitres et des concepts. Modifiez ce que vous voulez, puis acceptez.",
  "start.study.title": "Étudiez et répondez",
  "start.study.body": "Lisez un concept, puis répondez à ses questions avec vos mots ou à voix haute.",
  "start.review.title": "Révisez au bon moment",
  "start.review.body": "Les questions reviennent juste avant que vous ne les oubliiez. Suivez le bouton vert.",
  "nav.community": "Communauté",
  "community.title": "Communauté",
  "community.intro": "Découvrez qui gagne le plus d'expérience, suivez d'autres apprenants et encouragez-les.",
  "community.tab.leaderboard": "Classement",
  "community.tab.friends": "Amis",
  "community.period": "Période",
  "community.scope": "Qui",
  "community.period.day": "Aujourd'hui",
  "community.period.week": "Cette semaine",
  "community.period.month": "Ce mois-ci",
  "community.period.all": "Depuis toujours",
  "community.scope.everyone": "Tout le monde",
  "community.scope.friends": "Amis",
  "community.leader": "En tête",
  "community.xp": "{n} XP",
  "community.you": "Vous",
  "community.yourPlace": "Votre place",
  "community.empty": "Personne n'a encore gagné d'XP sur cette période.",
  "community.emptyFriends": "Suivez d'autres apprenants pour vous comparer à eux ici.",
  "community.windowNote": "Les jours suivent l'heure UTC ; les semaines commencent le lundi. Seules les personnes ayant rejoint la communauté apparaissent.",
  "community.joinTitle": "Rejoignez la communauté",
  "community.joinText": "Vous n'êtes pas encore dans le classement. Activez la visibilité dans la communauté pour y apparaître, être trouvé et suivi. Les autres ne voient que votre nom affiché et vos XP, jamais votre e-mail.",
  "community.joinButton": "M'afficher dans la communauté",
  "community.needName": "Choisissez d'abord un nom affiché dans les Paramètres.",
  "community.follow": "Suivre",
  "community.following": "Abonné",
  "community.friend": "Amis",
  "community.followsYou": "Vous suit",
  "community.since": "Apprend ici depuis le {date}",
  "community.statXpTotal": "XP totaux",
  "community.statXpWeek": "XP cette semaine",
  "community.statStreak": "Série de jours",
  "community.statFollowers": "Abonnés",
  "community.statFollowing": "Abonnements",
  "community.followingList": "Personnes que vous suivez",
  "community.followersList": "Vos abonnés",
  "community.searchLabel": "Trouver des apprenants par nom",
  "community.searchPlaceholder": "Rechercher par nom affiché",
  "community.search": "Rechercher",
  "community.noResults": "Personne trouvé. Seules les personnes ayant rejoint la communauté peuvent être trouvées.",
  "community.noFollowing": "Vous ne suivez encore personne.",
  "community.noFollowers": "Pas encore d'abonnés.",
  "community.streakDays": "Série de {n} jours",
  "community.xpToday": "{n} XP aujourd'hui",
  "community.notFound": "Ce profil n'est pas disponible.",
  "community.seeLeaderboard": "Voir le classement de la communauté",
  "course.leaderboardLink": "Classement du cours",
  "course.leaderboardTitle": "Classement du cours",
  "course.leaderboardIntro": "XP gagnés dans ce cours par tous ceux qui le suivent.",
  "course.leaderboardPrivate": "Ce cours est privé : vous êtes seul dans son classement. Les cours du Marketplace sont partagés avec tous ceux qui les ont.",
  "course.leaderboardParticipants": "{n} apprenants partagent ce cours.",
  "settings.community": "Communauté",
  "settings.communityToggle": "M'afficher dans la communauté (classements, recherche et abonnés)",
  "settings.communityHint": "Désactivé par défaut. Les autres ne voient que votre nom affiché, vos XP et votre série, jamais votre e-mail, vos cours ou vos réponses. Il faut un nom affiché pour l'activer.",
  "study.repeat.badge": "Presque : répétez-la",
  "study.repeat.title": "Relisez la réponse de référence, puis répétez-la",
  "study.repeat.body": "Ta réponse était solide sur la plupart des points. Lis la réponse de référence ci-dessus, puis réécris-la avec tes mots. Si tu la répètes bien, elle compte comme correcte et ton planning avance. Sinon, l'élément retourne au niveau zéro.",
  "study.repeat.label": "Votre réponse, à nouveau",
  "study.repeat.submit": "Répéter",
  "study.repeat.selfGrade": "La noter vous-même à la place",
  "study.repeat.evalTitle": "Ta répétition",
  "error.generic": "Un problème est survenu. Veuillez réessayer.",
  "error.transport": "Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.",
  "error.session": "Votre session a expiré. Veuillez vous reconnecter.",
  "error.validation": "Certaines informations saisies ne sont pas valides.",
  "error.notFound": "Cet élément n'existe plus.",
  "error.tooLarge": "Ce fichier est trop volumineux.",
  "error.unsupported": "Ce type de fichier n'est pas pris en charge. Utilisez des fichiers PDF, Word, PowerPoint, texte ou Markdown.",
  "error.aiNotConfigured": "L'IA n'est pas encore configurée sur le serveur.",
  "error.aiUnavailable": "L'IA est temporairement indisponible. Veuillez réessayer plus tard.",
  "error.aiInvalid": "L'IA a donné une réponse inutilisable. Veuillez réessayer.",
  "error.state": "Cette action n'est pas possible dans l'état actuel.",
  "error.server": "Un problème est survenu sur le serveur. Veuillez réessayer.",
  "error.rateLimited": "Trop de tentatives. Patientez un peu et réessayez.",
  "error.managedCourse": "Ce cours vient du Marketplace : son auteur le tient à jour, son contenu ne peut donc pas être modifié ici.",
  "studyState.NOT_STUDIED": "Non étudié",
  "studyState.STUDIED": "Étudié",
  "studyState.ACTIVE": "Actif",
  "studyState.PAUSED": "En pause",
  "studyState.COMPLETED": "Terminé",
  "memoryState.NEW": "Nouvelle",
  "memoryState.LEARNING": "En apprentissage",
  "memoryState.REVIEW": "En révision",
  "memoryState.RELEARNING": "À réapprendre",
  "memoryState.MASTERED": "Maîtrisée",
  "outcome.AGAIN": "À revoir",
  "outcome.HARD": "Difficile",
  "outcome.GOOD": "Bien",
  "outcome.EASY": "Facile",
  "outcomeHelp.AGAIN": "Je ne le savais pas",
  "outcomeHelp.HARD": "Je le savais, avec difficulté",
  "outcomeHelp.GOOD": "Je le savais",
  "outcomeHelp.EASY": "Je le savais facilement",
  "classification.CORRECT": "Correcte",
  "classification.PARTIALLY_CORRECT": "Partiellement correcte",
  "classification.MISCONCEPTION": "Idée fausse",
  "classification.WRONG": "Incorrecte",
  "classification.UNCERTAIN": "Incertaine",
  "role.CORE_TRAINABLE": "Essentiel",
  "role.SUPPORTING_TRAINABLE": "De soutien",
  "role.COMMON_TRAP": "Piège courant",
  "role.INFORMATIONAL": "Informative",
  "role.REFERENCE": "Référence",
  "role.OPTIONAL_EXTENSION": "Facultative",
  "priority.1": "Essentielle",
  "priority.2": "Importante",
  "priority.3": "Bonus",
  "label.page": "Page {n}",
  "label.pages": "Pages {a}–{b}",
  "label.pageShort": "p. {n}",
  "label.pagesShort": "pp. {a}–{b}",
  "category.law": "Droit",
  "category.economics_business": "Économie et entreprise",
  "category.accounting_finance": "Comptabilité et finance",
  "category.medicine_health": "Médecine et santé",
  "category.sciences": "Sciences",
  "category.mathematics": "Mathématiques",
  "category.engineering": "Ingénierie",
  "category.computer_science": "Informatique",
  "category.languages": "Langues",
  "category.humanities": "Histoire et sciences humaines",
  "category.arts": "Arts et musique",
  "category.professional_exams": "Examens professionnels",
  "category.other": "Autre",
  "level.all": "Tous niveaux",
  "level.beginner": "Débutant",
  "level.intermediate": "Intermédiaire",
  "level.advanced": "Avancé",
  "size.small": "Court (moins de 50 questions)",
  "size.medium": "Moyen (50–299 questions)",
  "size.large": "Grand (300+ questions)",
  "sort.popular": "Les plus suivis",
  "sort.newest": "Plus récents",
  "sort.largest": "Plus de questions",
  "lang.it": "Italien",
  "lang.en": "Anglais",
  "price.free": "Gratuit",
  "common.loading": "Chargement…",
  "common.tryAgain": "Réessayer",
  "common.dismiss": "Ignorer",
  "common.delete": "Supprimer",
  "common.edit": "Modifier",
  "common.rename": "Renommer",
  "common.title": "Titre",
  "common.description": "Description",
  "common.continue": "Continuer",
  "common.search": "Rechercher",
  "common.add": "Ajouter",
  "common.remove": "Retirer",
  "common.yes": "Oui",
  "common.no": "Non",
  "common.collapse": "Réduire {label}",
  "common.expand": "Développer {label}",
  "drag.item": "Élément",
  "drag.reorder": "Réorganiser {label}",
  "drag.pickedUp": "{label} saisi, position {pos} sur {total}.",
  "drag.over": "{label} est maintenant en position {pos} sur {total}.",
  "drag.dropped": "{label} déposé en position {pos} sur {total}.",
  "drag.droppedPlain": "{label} déposé.",
  "drag.cancelled": "Déplacement de {label} annulé.",
  "drawing.loadFailed": "Impossible de charger le dessin.",
  "course.authorOrder": "L'ordre de ce cours est fixé par son auteur.",
  "priority.yours": "Votre priorité",
  "priority.label": "Priorité",
  "priority.author": "Auteur : {name}",
  "priority.useAuthor": "Utiliser celle de l'auteur",
  "editTitle.rename": "Renommer {label}",
  "label.course": "cours",
  "label.chapter": "chapitre",
  "label.topic": "thème",
  "label.concept": "concept",
  "library.heading": "Mes cours",
  "library.count.one": "{n} cours",
  "library.count.other": "{n} cours",
  "library.shownOf": "{shown} sur {total} affichés",
  "library.search": "Rechercher des cours",
  "library.show": "Afficher",
  "library.sortBy": "Trier par",
  "library.new": "+ Nouveau cours",
  "library.marketplace": "🛒 Marketplace",
  "library.empty": "Aucun cours pour l'instant. Créez-en un, puis ajoutez votre matériel d'étude ou vos propres questions et réponses.",
  "library.createFirst": "Créez votre premier cours",
  "library.noMatch": "Aucun cours ne correspond à cette recherche ou à ce filtre.",
  "libStatus.all": "Tous les cours",
  "libStatus.in_progress": "En cours",
  "libStatus.not_started": "Non commencés",
  "libStatus.due": "Révisions à faire",
  "libStatus.completed": "Rien de nouveau à apprendre",
  "libStatus.paused": "En pause",
  "libStatus.archived": "Archivés",
  "libSort.recent_study": "Étudiés récemment",
  "libSort.recent_created": "Créés récemment",
  "libSort.alphabetical": "Alphabétique",
  "libSort.most_due": "Plus de révisions à faire",
  "card.marketplacePill": "🛒 Du marketplace · par {author}",
  "card.marketplaceHint": "Son auteur tient le contenu à jour ; votre étude vous appartient.",
  "card.continue": "Continuer",
  "card.next": "Suivant",
  "card.archived": "Archivé",
  "card.paused": "En pause",
  "card.conceptsStudied": "concepts étudiés",
  "card.itemsIntroduced": "éléments d'étude introduits",
  "card.moreFor": "Plus pour {title}",
  "menu.details": "Détails du cours",
  "menu.material": "Matériel d'étude",
  "menu.progress": "Progrès",
  "menu.publish": "🛒 Publier sur le marketplace",
  "menu.pause": "Mettre en pause",
  "menu.resume": "Reprendre",
  "menu.archive": "Archiver",
  "menu.restore": "Restaurer",
  "menu.delete": "Supprimer…",
  "menu.confirmOwn": "Supprimer « {title} » ? Ses chapitres, questions, supports et vos réponses seront supprimés définitivement. Archivez-le plutôt pour tout conserver.",
  "menu.confirmMarketplace": "Supprimer « {title} » ? Vous pourrez le rajouter depuis le Marketplace gratuitement.",
  "newCourse.aria": "Nouveau cours",
  "newCourse.language": "Langue du matériel",
  "newCourse.create": "Créer le cours",
  "learn.resume.label": "Reprendre l'entraînement",
  "learn.resume.hint": "Continuez le tour laissé inachevé",
  "learn.study.label": "Apprendre la suite",
  "learn.study.hint": "Étudier {title}",
  "learn.study.hintNone": "Apprendre du nouveau matériel",
  "learn.activate.label": "Commencer à apprendre",
  "learn.activate.hint": "Préparez les questions de {title} et commencez à l'étudier",
  "learn.activate.hintNone": "Commencez à étudier le concept suivant",
  "learn.setup.label": "Ajouter du matériel",
  "learn.setup.hint": "Ajoutez du matériel d'étude ou vos propres questions pour commencer",
  "learn.none.label": "Tout appris ✓",
  "learn.none.hint": "Tout dans ce cours a été appris",
  "review.none": "✓ Rien à réviser maintenant",
  "review.noneTitle": "Rien dans ce cours ne demande de révision pour l'instant",
  "review.aria.one": "Réviser {n} élément",
  "review.aria.other": "Réviser {n} éléments",
  "review.button": "Réviser",
  "course.practiceHard": "S'entraîner sur les questions difficiles",
  "course.chapters": "Chapitres",
  "course.chaptersEmpty": "Ajoutez un chapitre, puis du matériel d'étude.",
  "course.chapterMeta": "{active} concepts actifs sur {total} · maîtrise {mastery}",
  "course.newChapter": "Titre du nouveau chapitre",
  "course.addChapter": "Ajouter un chapitre",
  "course.curriculum": "Programme",
  "course.fallback": "Cours",
  "course.allMaterial": "Tout le matériel d'étude",
  "course.manageQuestions": "Gérer les questions",
  "course.weakSpots": "Ce que vous ratez encore",
  "chapter.gone": "Ce chapitre n'existe plus.",
  "chapter.topics": "Thèmes",
  "chapter.starting": "Démarrage de l'analyse…",
  "chapter.analyze": "Analyser le nouveau matériel",
  "chapter.analyzeHint": "L'IA propose des thèmes et des concepts à partir du matériel pas encore analysé. Vous relisez la proposition avant tout changement.",
  "chapter.noMaterial": "Il n'y a pas de nouveau matériel à analyser dans ce chapitre. Importez d'abord du matériel.",
  "chapter.empty": "Aucun thème pour l'instant. Importez du matériel ci-dessous, puis analysez-le.",
  "chapter.topicMeta": "{active} concepts actifs sur {total}",
  "topic.gone": "Ce thème n'existe plus.",
  "topic.concepts": "Concepts",
  "topic.empty": "Aucun concept dans ce thème.",
  "topic.needsReview": "Source à revoir",
  "topic.conceptMeta": "{state} · maîtrise {mastery}",
  "unit.item.one": "{n} élément",
  "unit.item.other": "{n} éléments",
  "unit.answer.one": "{n} réponse",
  "unit.answer.other": "{n} réponses",
  "consolidate.continue": "Poursuivre la consolidation",
  "consolidate.unfinished": "Ton lot de {items} n'est pas terminé : il en reste {left}. Les réponses déjà données sont conservées.",
  "consolidate.resume": "Reprendre",
  "consolidate.doneTitle": "Consolidé",
  "consolidate.doneBody": "Chaque élément de ce concept a eu ses tours de consolidation. Ils reviendront dans vos révisions à l'échéance.",
  "consolidate.title": "Concept étudié ?",
  "consolidate.body": "Lis d'abord ce qu'il faut étudier ci-dessus. Quand tu es prêt, chaque question est reposée jusqu'à ce que tu y répondes bien {n} fois (pas forcément de suite), avec un retour après chaque réponse.",
  "consolidate.batch": "Ce lot : {items} · {answers}",
  "consolidate.more.one": "{n} élément de plus attend un lot ultérieur",
  "consolidate.more.other": "{n} éléments de plus attendent un lot ultérieur",
  "consolidate.button": "J'ai étudié ce concept",
  "breadcrumb.aria": "Fil d'Ariane",
  "dash.title": "Tableau de bord",
  "dash.loading": "Chargement de votre tableau de bord…",
  "dash.aside": "Votre étude",
  "dash.today": "Aujourd'hui",
  "dash.xpTodayPlus": "+{n} XP aujourd'hui",
  "dash.goal": "Objectif {done}/{target}",
  "dash.tzNote": "Vos jours d'étude (série, objectif, XP du jour) suivent {zone}. Cet appareil est en {device}.",
  "dash.tzUse": "Utiliser {zone}",
  "next.title": "Votre prochaine étape",
  "next.resumeTitle": "Continuer la consolidation · Bonne réponse {round} sur {total}",
  "next.answersLeft": "{n} réponses restantes",
  "next.resume": "Reprendre",
  "next.minutes": "environ {n} min",
  "next.startReview": "Commencer la révision",
  "next.learnTitle": "Continuez à apprendre : {concept}",
  "next.nextConcept": "concept suivant",
  "next.learn": "Apprendre",
  "next.setupTitle": "Préparer {concept}",
  "next.theNextConcept": "le concept suivant",
  "next.setupDetail": "{course} · activez-le pour préparer ses questions",
  "next.setup": "Préparer",
  "next.addMaterialTitle": "Ajoutez du matériel à {course}",
  "next.yourCourse": "votre cours",
  "next.addMaterialDetail": "Importez du matériel d'étude ou vos propres questions et réponses pour construire ses concepts.",
  "next.firstCourse": "Commencez votre premier cours",
  "next.firstCourseDetail": "Créez un cours, puis ajoutez du matériel ou vos propres questions.",
  "next.caughtUp": "Vous êtes à jour",
  "next.caughtUpDetail": "Rien n'est à faire et aucun nouveau matériel n'attend.",
  "streak.title": "Série quotidienne",
  "streak.unit.one": "jour",
  "streak.unit.other": "jours",
  "streak.done": "Aujourd'hui, c'est fait. Revenez demain pour continuer.",
  "streak.keep": "Répondez à une question aujourd'hui pour garder votre série.",
  "streak.start": "Répondez à une question aujourd'hui pour commencer une série.",
  "streak.last7": "Les 7 derniers jours",
  "streak.studied": "étudié",
  "streak.noStudy": "pas d'étude",
  "xp.title": "Expérience",
  "xp.how": "Comment fonctionnent les XP",
  "xp.howBody": "Chaque bonne réponse au même élément rapporte plus : 10 XP la première fois, puis 20, 30… jusqu'à 150. Un indice divise par deux les XP de cette réponse. Les mauvaises réponses ne rapportent rien mais ne réinitialisent pas votre progression. Les XP récompensent la pratique ; ils ne modifient jamais votre planning de révision.",
  "goal.title": "Objectif quotidien",
  "goal.sr": "Objectif quotidien : {done} réponses terminées sur {target} aujourd'hui, {percent} pour cent.",
  "goal.answersToday": "{done} / {target} réponses aujourd'hui",
  "goal.perDay": "Réponses par jour",
  "goal.edit": "Modifier l'objectif",
  "goal.invalid": "Choisissez un nombre entier de 1 à 500.",
  "planner.title": "Planificateur",
  "planner.now": "Maintenant",
  "planner.1h": "Dans 1 heure",
  "planner.4h": "Dans 4 heures",
  "planner.1d": "Dans 1 jour",
  "planner.3d": "Dans 3 jours",
  "planner.7d": "Dans 7 jours",
  "planner.dueNow": "{n} à faire maintenant : environ {min} min",
  "planner.caption": "Révisions à faire pour chaque échéance, y compris celles en retard",
  "planner.dueBy": "Échéance",
  "planner.items": "Éléments",
  "planner.hint": "Basé sur votre planning actuel ; il change au fil de l'étude. Chaque ligne inclut tout ce qui est dû avant.",
  "activity.title": "Activité",
  "activity.seeMore": "Voir plus",
  "activity.future": "{day} : dans le futur",
  "activity.day": "{day} : {answers}",
  "activity.dayXp": "{day} : {answers}, {xp} XP",
  "activity.aria": "Calendrier d'activité : {days}, {answers}",
  "activity.summary": "{days} · {answers}",
  "unit.activeDay.one": "{n} jour actif",
  "unit.activeDay.other": "{n} jours actifs",
  "activity.less": "Moins",
  "activity.more": "Plus",
  "activity.legendFuture": "Futur",
  "pb.curriculumHint": "Quelle part du matériel vous avez étudiée et activée.",
  "pb.conceptsActive": "Concepts actifs",
  "pb.activeOf": "{active} sur {total}",
  "pb.memory": "Mémoire",
  "pb.memoryHint": "À quel point vous retenez ce que vous travaillez. La maîtrise est une estimation tirée de vos révisions.",
  "pb.mastery": "Maîtrise (estimation)",
  "pb.itemsTrained": "Éléments en entraînement",
  "pb.markedHard": "Marqués difficiles",
  "pb.dueNow": "À faire maintenant",
  "pb.laterToday": "Plus tard aujourd'hui",
  "pb.tomorrow": "Demain",
  "pb.next7": "7 prochains jours",
  "pb.newToLearn": "Nouveaux à apprendre",
  "pb.overdue.one": "{n} question est en retard : commencez par celles-là.",
  "pb.overdue.other": "{n} questions sont en retard : commencez par celles-là.",
  "pb.caption": "Questions à venir dans ce cours et temps approximatif",
  "pb.when": "Quand",
  "pb.questions": "Questions",
  "pb.time": "Durée",
  "pb.minutes": "~{n} min",
  "study.intent.LEARN": "Apprendre",
  "study.intent.SCHEDULED_REVIEW": "Révision",
  "study.intent.PRACTICE": "Entraînement",
  "study.intent.EXAM": "Examen",
  "study.intent.CONSOLIDATION": "Consolidation",
  "study.round": "Bonne réponse {n} sur {total}",
  "study.progressAria": "Progression dans cette session",
  "study.progress": "{n} sur {total}",
  "study.xpSessionAria": "{n} XP dans cette session",
  "study.ownReview": "Révision libre : aucun changement de planning, pas d'XP",
  "study.leaveResume": "Quitter (reprendre plus tard)",
  "study.end": "Terminer la session",
  "study.preparing": "Préparation de votre session…",
  "study.empty.title": "Rien à étudier maintenant",
  "study.empty.body": "Rien ne convient à cette session. Activez un concept pour ajouter du nouveau matériel, ou revenez quand des révisions seront dues.",
  "study.empty.back": "Retour au cours",
  "study.newMaterial": "Nouveau matériel",
  "study.keyPoints": "Points clés",
  "study.sources": "Sources",
  "study.ready": "Je suis prêt : testez-moi",
  "study.answering": "Répondre",
  "study.drawHint": "Dessinez votre réponse ci-dessous. L'IA la compare au dessin de référence.",
  "study.note": "Note (facultative)",
  "study.yourAnswer": "Votre réponse",
  "study.notePlaceholder": "Quelque chose à ajouter à votre dessin…",
  "study.answerPlaceholder": "Répondez avec vos propres mots…",
  "study.stopListening": "Arrêter l'écoute",
  "study.speak": "Dites votre réponse",
  "study.evaluating": "Évaluation en cours…",
  "study.submit": "Envoyer",
  "study.skip": "Passer cette question",
  "study.dictationOn": "La dictée est transcrite par votre navigateur, qui peut envoyer l'audio à son service vocal. Seul le texte est conservé ; modifiez-le avant d'envoyer.",
  "study.dictationOff": "Les réponses vocales nécessitent Chrome, Edge ou Safari. Vous pouvez saisir votre réponse ici.",
  "study.drawingCompare": "Votre dessin et la référence",
  "study.yourDrawing": "Votre dessin",
  "study.reference": "Référence",
  "study.gradedByYou": "Noté par vous",
  "study.needsGrade": "Attend votre note",
  "study.firstEval": "Première évaluation",
  "study.secondOpinion": "Second avis",
  "study.afterObjection": "Après votre objection : « {text} »",
  "study.referenceSummary": "Réponse de référence et sources",
  "study.studyConcept": "📖 Étudier ce concept",
  "study.gradeHow": "À quel point le saviez-vous ?",
  "study.gradeYourself": "Ou notez-vous vous-même",
  "study.yourGrade": "Votre note",
  "study.retryEval": "Réessayer l'évaluation",
  "study.skipNow": "Passer pour l'instant",
  "study.disagree": "Pas d'accord avec la note ?",
  "study.notMemorized": "Pas encore mémorisé : vous le reverrez dans cette session.",
  "study.levelNext": "Niveau {from} → {to}. Prochaine révision : {when}",
  "study.practiceNote": "L'entraînement ne modifie pas votre planning de révision.",
  "study.complete": "Session terminée",
  "study.xpSessionPlus": "+{n} XP dans cette session",
  "study.answeredIn": "{answers} en environ {min} min.",
  "study.wentWell": "{n} se sont bien passées.",
  "study.steady": "Travail régulier : cela s'installe.",
  "study.comeBack": "Celles qui se sont mal passées reviennent bientôt, c'est ainsi qu'elles s'ancrent.",
  "study.stillDue": "Encore à faire maintenant : {n} (environ {min} min).",
  "study.nothingDue": "Rien d'autre n'est dû maintenant.",
  "study.done": "Terminé",
  "study.noXp": "Cette réponse ne rapporte pas d'XP (entraînement ou pas encore à réviser).",
  "study.correctXp": "Bonne réponse : +{n} XP",
  "study.halfXp": "(indice utilisé : moitié des XP)",
  "study.hintLabel": "Indice",
  "study.noHint": "Pas d'indice pour cette question.",
  "study.showHint": "Afficher l'indice",
  "study.hintHalves": "Utiliser un indice divise par deux les XP de cette réponse.",
  "study.revealHint": "Révéler l'indice",
  "study.plusZero": "+0 XP",
  "study.onlyCorrectSelf": "Seules les bonnes réponses rapportent des XP : vos notes Difficile, Bien ou Facile comptent aussi.",
  "study.onlyCorrect": "Seules les réponses entièrement correctes rapportent des XP. Votre progression vers la prochaine récompense est conservée.",
  "study.awardCorrect": "Correcte",
  "study.awardHint": "Indice utilisé",
  "study.ordinal": "bonne réponse n° {n} pour cet élément",
  "study.ordinalHalf": "bonne réponse n° {n} pour cet élément, la moitié de {base}",
  "study.batchDone": "Lot consolidé",
  "study.correctAnswers": "Bonnes réponses",
  "study.xpThisSession": "XP de cette session",
  "study.whatNext": "Et ensuite",
  "study.whatNextBody": "Les éléments dont le dernier tour était juste reviennent en révision dans environ 4 heures ; les autres attendent le lot suivant.",
  "study.waiting.one": "{n} élément de plus attend",
  "study.waiting.other": "{n} éléments de plus attendent",
  "study.batchNotAll": "Ce lot est terminé, pas tout le concept : {waiting}.",
  "study.allRounds": "Chaque élément de ce concept en attente a reçu trois bonnes réponses.",
  "study.nextBatch": "Lot suivant",
  "study.conceptCosts": "Consulter le concept maintenant compte comme un indice : cette réponse rapporte la moitié des XP.",
  "study.openConcept": "Ouvrir le concept",
  "study.dontRemember": "📖 Vous ne vous en souvenez pas ? Étudiez ce concept",
  "study.refDrawing": "Le dessin de référence",
  "dispute.title": "Demandez à l'IA de réexaminer",
  "dispute.testAi": "L'IA de test ne peut pas lire votre objection : un second avis répéterait la même note. Notez-vous vous-même ci-dessous.",
  "dispute.why": "Pourquoi n'êtes-vous pas d'accord ?",
  "dispute.placeholder": "Dites ce que l'évaluation a mal jugé…",
  "dispute.hint": "L'IA voit votre objection et évalue à nouveau. Les deux avis restent visibles ; votre note ne change que si vous la choisissez ci-dessous.",
  "dispute.asking": "Demande en cours…",
  "dispute.ask": "Demander un second avis",
  "eval.correct": "Correct",
  "eval.complete": "Complet",
  "eval.understanding": "Compréhension",
  "eval.precise": "Précis",
  "eval.Strong": "Solide",
  "eval.Partial": "Partiel",
  "eval.Weak": "Faible",
  "eval.scoreBreakdown": "Détail des scores",
  "eval.right": "Juste",
  "eval.missing": "Manquant",
  "eval.misconception": "Idée fausse",
  "eval.materialSays": "Le matériel indique",
  "eval.covered": "Ce que couvrait votre réponse",
  "eval.failed": "La réponse n'a pas pu être évaluée.",
  "eval.aiEvaluation": "Évaluation de l'IA : {verdict}",
  "eval.insufficient": "Le matériel du cours ne couvre pas cela assez bien pour le noter.",
  "eval.testAiTitle": "IA de test.",
  "eval.testAiBody": "Cette note vient d'un substitut qui ne fait que comparer des mots avec la référence : il ne comprend ni les paraphrases ni les objections. Vérifiez la référence et notez-vous vous-même, ou connectez une vraie IA.",
  "conceptPanel.title": "Étudier ce concept",
  "conceptPanel.next": "J'ai étudié : question suivante",
  "conceptPanel.closeAria": "Fermer le concept",
  "conceptPanel.loading": "Chargement du concept…",
  "conceptPanel.openFull": "Ouvrir la page complète du concept dans un nouvel onglet",
  "dictation.blocked": "Le micro est bloqué. Autorisez-le dans les paramètres du site de votre navigateur pour dicter.",
  "dictation.network": "La reconnaissance vocale nécessite une connexion Internet.",
  "dictation.noMic": "Aucun micro n'a été trouvé.",
  "dictation.stopped": "La reconnaissance vocale s'est arrêtée de façon inattendue. Vous pouvez continuer à écrire.",
  "concept.activate": "Activer",
  "concept.activateHelp": "Prépare les questions de ce concept à partir de votre matériel pour que vous puissiez l'étudier.",
  "concept.markStudied": "Marquer comme étudié",
  "concept.markStudiedHelp": "Vous l'avez étudié ailleurs : c'est noté, sans préparer de questions pour l'instant.",
  "concept.activateAgainHelp": "L'étudie à nouveau : ses questions reviennent dans vos sessions.",
  "concept.pause": "Mettre en pause",
  "concept.pauseHelp": "Suspend ses questions un moment. Rien n'est perdu.",
  "concept.deactivate": "Désactiver",
  "concept.deactivateHelp": "Le retire de l'étude et des révisions. Vos réponses et votre historique sont conservés.",
  "concept.resume": "Reprendre",
  "concept.resumeHelp": "Remet ses questions dans l'étude et les révisions.",
  "concept.sourceDeleted": "Le matériel dont provenait ce concept a été supprimé. Conservez-le, modifiez-le ou supprimez-le.",
  "concept.manage": "Gérer ce concept",
  "concept.loadingItems": "Chargement des éléments d'étude…",
  "concept.preparing": "Préparation des éléments d'étude…",
  "concept.itemsFailed": "Les éléments d'étude n'ont pas pu être préparés.",
  "concept.noSource": "Ce concept n'a pas de matériel source pour construire des éléments d'étude.",
  "concept.whatToStudy": "Quoi étudier",
  "concept.reviewOwnHelp": "Faites-vous interroger sur ce concept quand vous voulez. Cela ne change pas votre planning et ne rapporte pas d'XP.",
  "concept.reviewOwn": "🔁 Réviser seul",
  "concept.readThen": "Lisez-les, puis appuyez sur « J'ai étudié ce concept » ci-dessous pour vous entraîner.",
  "concept.testMe": "🔁 Testez-moi là-dessus",
  "concept.hideDetails": "Masquer les détails",
  "concept.details": "Détails",
  "concept.level": "Niveau {n}",
  "concept.markedHard": "Marqué difficile",
  "concept.notTraining": "Hors entraînement",
  "concept.state": "État",
  "concept.levelLabel": "Niveau",
  "concept.nextReview": "Prochaine révision",
  "concept.reviews": "Révisions",
  "concept.lapses": "Oublis",
  "concept.inTraining": "En entraînement",
  "concept.questions": "Questions",
  "concept.sourcesTitle": "Sources",
  "concept.loadingSources": "Chargement des sources…",
  "concept.noSources": "Aucun passage source.",
  "concept.misconceptions": "Idées fausses récurrentes",
  "drawing.answerBy": "Répondre en dessinant",
  "drawing.studentsDraw": "Les étudiants répondent à cette question en dessinant. L'IA compare leur dessin à celui-ci.",
  "drawing.youDraw": "Vous répondez à cette question en dessinant. L'IA compare votre dessin à celui-ci.",
  "drawing.referenceAlt": "Dessin de référence",
  "drawing.explain": "Pour les questions à réponse dessinée (une molécule, un schéma, un graphique) : ajoutez le dessin correct et les étudiants dessineront leur réponse au lieu de l'écrire.",
  "drawing.save": "Enregistrer ce dessin",
  "drawing.replace": "Remplacer par une image…",
  "drawing.upload": "Importer le dessin…",
  "drawing.drawNew": "En dessiner un nouveau",
  "drawing.drawHere": "Dessinez-le ici",
  "drawing.words": "Répondre avec des mots",
  "drawing.tools": "Outils de dessin",
  "drawing.pen": "✏️ Stylo",
  "drawing.eraser": "🧽 Gomme",
  "drawing.thick": "Trait épais",
  "drawing.thin": "Trait fin",
  "drawing.undo": "↶ Annuler",
  "drawing.clear": "Effacer",
  "drawing.area": "Zone de dessin : dessinez votre réponse ici",
  "drawing.photoUpload": "Dessiné sur papier ? Importez plutôt une photo",
  "drawing.photoError": "Ce fichier n'est pas une image lisible par ce navigateur. Essayez une photo JPEG ou PNG.",
  "drawing.photoAlt": "Votre photo du dessin",
  "drawing.drawInstead": "Dessiner plutôt",
  "q.title": "Questions",
  "q.introOwn": "Sélectionnez des questions pour les supprimer, les mettre en pause, les reprendre ou les déplacer ; faites glisser ⠿ pour les réordonner ; modifiez-en une pour corriger son titre, sa formulation ou sa réponse attendue.",
  "q.introManaged": "Ce cours vient du Marketplace : son auteur tient les questions à jour. Sélectionnez des questions pour les mettre en pause ou les reprendre, ou donnez-leur votre propre priorité (vous seul la voyez).",
  "q.search": "Fragen suchen",
  "q.searchPlaceholder": "Rechercher des questions et réponses",
  "q.allPriorities": "Toutes les priorités",
  "q.priorityOnly": "{label} uniquement ({n})",
  "q.deselectAll": "Tout désélectionner",
  "q.selectAll": "Tout sélectionner ({n})",
  "q.selectAllShown": "Sélectionner tout ce qui est affiché ({n})",
  "q.collapseAll": "Tout réduire",
  "q.expandAll": "Tout développer",
  "q.selectedAria": "Questions sélectionnées",
  "q.selectedCount": "{n} sélectionnées",
  "q.moveTo": "Déplacer vers…",
  "q.setPriority": "Définir la priorité",
  "q.setPriorityPlaceholder": "Définir la priorité…",
  "q.reviewSelectedHelp": "Faites-vous interroger maintenant sur les questions sélectionnées. Cela ne change pas votre planning et ne rapporte pas d'XP.",
  "q.clearSelection": "Effacer la sélection",
  "q.verb.delete": "Supprimées",
  "q.verb.pause": "Mises en pause",
  "q.verb.resume": "Reprises",
  "q.verb.move": "Déplacées",
  "q.verb.set_priority": "Mises à jour",
  "unit.question.one": "{n} question",
  "unit.question.other": "{n} questions",
  "unit.chapter.one": "{n} chapitre",
  "unit.chapter.other": "{n} chapitres",
  "unit.topic.one": "{n} thème",
  "unit.topic.other": "{n} thèmes",
  "unit.concept.one": "{n} concept",
  "unit.concept.other": "{n} concepts",
  "unit.group.one": "{n} groupe vidé",
  "unit.group.other": "{n} groupes vidés",
  "q.newConcepts.one": "{n} nouveau concept",
  "q.newConcepts.other": "{n} nouveaux concepts",
  "q.removedConcepts.one": "{n} concept vide supprimé",
  "q.removedConcepts.other": "{n} concepts vides supprimés",
  "q.noticeBulk": "{verb} {questions}{extra}.",
  "q.noticeDeleted": "Supprimé : {questions}.",
  "q.noticeDeletedGroups": "Supprimé : {questions} et {groups}.",
  "q.confirmDelete": "Supprimer {questions} ? Leurs réponses et leur historique de révision sont aussi supprimés.{also}",
  "q.confirmAlso": " Les {groups} qui se retrouvent sans question sont aussi supprimés.",
  "q.emptySearch": "Aucune question ne correspond à cette recherche.",
  "q.empty": "Aucune question pour l'instant. Activez un concept ou importez vos propres questions et réponses.",
  "q.clearToReorder": "Effacez la recherche pour réordonner ces questions.",
  "q.select": "Sélectionner : {text}",
  "q.otherWordings.one": "+{n} autre formulation",
  "q.otherWordings.other": "+{n} autres formulations",
  "q.editAria": "Modifier la question",
  "q.titleLabel": "Titre (affiché dans les listes)",
  "q.wordings": "Formulations de la question",
  "q.wording": "Formulation {n}",
  "q.addWording": "+ Ajouter une autre formulation",
  "q.expected": "Réponse attendue",
  "q.prio1": "Essentielle : le cœur, à connaître",
  "q.prio2": "Importante : à savoir",
  "q.prio3": "Bonus : complément, détail approfondi",
  "q.keyPoints": "Points clés (un par ligne, jusqu'à 10)",
  "q.moveAria": "Déplacer des questions",
  "q.moveHeading": "Déplacer {questions} vers",
  "q.choose": "Choisissez un thème ou un concept…",
  "q.topicOption": "Thème : {title} (chaque question garde son concept)",
  "q.conceptOption": "   ↳ dans le concept : {title}",
  "q.removeEmpty": "Supprimer les concepts restés sans question",
  "q.moveHint": "Les questions gardent leur progression de révision. Dans un concept non actif, elles ne sont pas révisées tant qu'il ne l'est pas.",
  "q.move": "Déplacer",
  "q.groupSelect": "Sélectionner toutes les questions de {label}",
  "mat.chapterTitle": "Matériel du chapitre",
  "mat.collapseLabel": "matériel d'étude",
  "mat.files.one": "{n} fichier",
  "mat.files.other": "{n} fichiers",
  "mat.uploading": "Envoi en cours…",
  "mat.upload": "Importer du matériel",
  "mat.uploadQa": "Importer questions et réponses",
  "mat.paste": "Coller du texte",
  "mat.howTo": "Comment préparer questions et réponses",
  "mat.howToBody": "Importez un fichier PDF, Word, texte ou Markdown où chaque question commence par « Domanda: » et sa réponse attendue par « Risposta: » (ou « Question: » / « Answer: »), en début de ligne. Les titres regroupent les questions en thèmes.",
  "mat.howToBody2": "Chaque question devient un concept marqué comme non étudié, avec votre réponse comme réponse attendue. Activez-le pour commencer à réviser. Aucune IA n'est utilisée pour l'import.",
  "mat.loading": "Chargement du matériel…",
  "mat.none": "Aucun matériel pour l'instant. Importez des fichiers PDF, Word, PowerPoint, texte ou Markdown.",
  "mat.file": "Fichier",
  "mat.status": "Statut",
  "mat.passages": "Passages",
  "mat.added": "Ajouté",
  "mat.actions": "Actions",
  "mat.qa": "Q&R",
  "mat.processing": "Traitement…",
  "mat.ready": "Prêt",
  "mat.failed": "Échec",
  "mat.download": "Télécharger",
  "mat.confirmDelete": "Supprimer {name} ? Les concepts construits à partir de lui seront signalés pour révision.",
  "mat.pasteAria": "Coller du texte",
  "mat.pastePlaceholder": "p. ex. notes du cours 3",
  "mat.thisTextIs": "Ce texte est",
  "mat.asMaterial": "Matériel d'étude",
  "mat.asQa": "Questions et réponses (Domanda: / Risposta:)",
  "mat.text": "Texte",
  "mat.textPlaceholderQa": "# Thème\nDomanda: …?\nRisposta: …",
  "mat.textPlaceholder": "# Titre\nVos notes…",
  "mat.adding": "Ajout en cours…",
  "mat.addText": "Ajouter le texte",
  "mat.pastedName": "Texte collé {when}",
  "prop.title": "Programme proposé",
  "prop.reading": "L'IA lit votre matériel…",
  "prop.applied": "Cette proposition a déjà été appliquée.",
  "prop.failed": "L'IA n'a pas pu proposer de programme.",
  "prop.blank": "Chaque chapitre, thème et concept a besoin d'un titre.",
  "prop.longMaterial": "Le matériel était long : cela couvre ses {used} premiers passages sur {total}.",
  "prop.dropped": "{n} concepts proposés ont été écartés car ils ne pouvaient pas être rattachés à votre matériel.",
  "prop.hint": "Modifiez tout ce qui suit : renommez, réordonnez, déplacez des concepts entre thèmes, fusionnez, supprimez ou ajoutez. Rien n'est activé : vous choisissez ensuite ce que vous étudiez.",
  "prop.chapterTitle": "Titre du chapitre",
  "prop.deleteChapter": "Supprimer le chapitre",
  "prop.accept": "Accepter le programme",
  "prop.reject": "Rejeter la proposition",
  "prop.topicAria": "Thème {title}",
  "prop.existingTopic": "thème existant",
  "prop.topicTitle": "Titre du thème",
  "prop.moveTopicUp": "Monter le thème",
  "prop.moveTopicDown": "Descendre le thème",
  "prop.deleteTopic": "Supprimer le thème",
  "prop.conceptTitle": "Titre du concept",
  "prop.exists": "existe déjà",
  "prop.moveConceptUp": "Monter le concept",
  "prop.moveConceptDown": "Descendre le concept",
  "prop.moveToTopic": "Déplacer vers le thème",
  "prop.untitled": "Thème sans titre",
  "prop.merge": "Fusionner avec le suivant",
  "prop.addConcept": "+ Ajouter un concept",
  "mkt.intro": "Des cours créés par d'autres utilisateurs de LEARNABLE. Ajoutez-en un à vos cours et étudiez-le avec votre propre planning : son auteur le tient à jour. Pour partager l'un des vôtres, ouvrez-le et remplissez sa « Page du marketplace ».",
  "mkt.categories": "Catégories",
  "mkt.all": "Toutes",
  "mkt.searchPlaceholder": "Rechercher par titre ou description",
  "mkt.level": "Niveau",
  "mkt.language": "Langue",
  "mkt.size": "Taille",
  "mkt.sort": "Trier",
  "mkt.any": "Tous",
  "mkt.loading": "Chargement des cours…",
  "mkt.noMatch": "Aucun cours ne correspond encore à ces filtres.",
  "mkt.publishedByMe": "Publiés par moi",
  "mkt.status.DRAFT": "Brouillon",
  "mkt.status.PUBLISHED": "Publié",
  "mkt.status.UNPUBLISHED": "Non publié",
  "mkt.version": "version {n}",
  "mkt.students.one": "{n} étudiant",
  "mkt.students.other": "{n} étudiants",
  "mkt.editCourse": "Modifier le cours et sa page",
  "mkt.by": "par {author}",
  "mkt.inYourCourses": "Dans vos cours",
  "mkt.loadingCourse": "Chargement du cours…",
  "mkt.updated": "mis à jour le {date}",
  "mkt.statStudents": "Étudiants",
  "mkt.yourCourse": "Votre cours",
  "mkt.draftTag": "brouillon",
  "mkt.notPublishedTag": "non publié",
  "mkt.open": "Ouvrir dans mes cours",
  "mkt.addBack": "Rajouter à mes cours",
  "mkt.add": "Ajouter à mes cours · {price}",
  "mkt.about": "À propos de ce cours",
  "mkt.learn": "Ce que vous apprendrez",
  "mkt.audience": "À qui il s'adresse",
  "mkt.hiddenNote": "Les questions et réponses deviennent visibles une fois le cours dans vos cours. Vous les étudiez dans LEARNABLE : le matériel de l'auteur ne peut pas être téléchargé.",
  "sales.originTitle": "🛒 Du marketplace",
  "sales.madeBy": "Créé par {author} (version {version}). Son auteur tient à jour les chapitres, questions et réponses ; vous ne pouvez donc pas les modifier ici : les mises à jour vous parviennent automatiquement.",
  "sales.yourStudy": "Votre étude vous appartient : activation des concepts, pauses, vos réponses, votre planning de révision et vos XP, ainsi que la priorité que vous donnez à chaque question (vous seul la voyez).",
  "sales.seePage": "Voir sa page du marketplace",
  "sales.title": "🛒 Page du marketplace",
  "sales.pPublished": "Publié · version {n}",
  "sales.pNot": "Non publié",
  "sales.pDraft": "Brouillon",
  "sales.pNone": "Non partagé",
  "sales.explain": "Partagez ce cours avec d'autres utilisateurs. Vous rédigez ici sa page, la seule chose que les gens voient avant de l'ajouter : ce qu'il couvre, à qui il s'adresse, ce qu'ils apprendront. Ils voient aussi les titres des chapitres et le nombre de questions ; les questions elles-mêmes seulement une fois qu'ils l'ont, et votre matériel importé jamais.",
  "sales.seeAsOthers": "Voir comme les autres",
  "sales.edit": "Modifier la page du marketplace",
  "sales.write": "Rédiger une page du marketplace",
  "sales.subtitle": "Sous-titre",
  "sales.subtitleHint": "(une ligne, p. ex. « Les 300 questions de l'oral, avec réponses types »)",
  "sales.category": "Catégorie",
  "sales.description": "Description",
  "sales.descriptionHint": "(ce qu'il couvre, comment vous l'avez conçu, comment l'utiliser)",
  "sales.outcomes": "Ce que les étudiants apprendront",
  "sales.outcomesHint": "(un par ligne, jusqu'à 8)",
  "sales.tags": "Étiquettes",
  "sales.tagsHint": "(séparées par des virgules, jusqu'à 8)",
  "sales.saveDraft": "Enregistrer le brouillon",
  "sales.savePage": "Enregistrer la page",
  "sales.publish": "Publier",
  "sales.publishUpdate": "Publier une mise à jour",
  "sales.publishHintUpdate": "Envoie vos chapitres, questions et réponses actuels à tous ceux qui ont le cours. Leur progression est conservée.",
  "sales.publishHint": "Rend le cours visible dans le Marketplace. C'est gratuit pour l'instant. Vous pouvez continuer à le modifier et publier des mises à jour plus tard.",
  "sales.rights": "Les questions et réponses m'appartiennent et je peux les partager (je les ai écrites, ou j'ai les droits sur le matériel dont elles proviennent).",
  "sales.publishing": "Publication…",
  "sales.publishUpdateBtn": "Publier la mise à jour",
  "sales.unpublish": "Dépublier",
  "sales.noteSaved": "Enregistré : la page du marketplace l'affiche maintenant.",
  "sales.noteDraft": "Brouillon enregistré. Personne d'autre ne le voit avant que vous publiiez.",
  "sales.notePublishedV": "Version {n} publiée : tous ceux qui ont le cours reçoivent maintenant vos dernières questions.",
  "sales.notePublished": "Publié ! Les autres utilisateurs peuvent maintenant le trouver dans le Marketplace.",
  "sales.noteUnpublished": "Dépublié : les nouveaux utilisateurs ne peuvent plus le trouver. Ceux qui l'ont déjà le gardent.",
  "prog.empty": "Créez un cours et commencez à apprendre pour voir votre progression.",
  "prog.weakConcepts": "Concepts fragiles",
  "prog.weakMeta": "oublis : {lapses} · marqués difficiles : {hard}",
  "prog.byChapter": "Par chapitre et thème",
  "prog.colChapter": "Chapitre / thème",
  "weak.intro": "Erreurs relevées dans vos réponses récentes, par concept. Elles disparaissent quand vos dernières réponses à l'élément ne les montrent plus. L'entraînement ne modifie pas votre planning de révision.",
  "weak.loading": "Examen de vos réponses récentes…",
  "weak.empty": "Aucune erreur récurrente pour l'instant. Continuez à répondre et elles apparaîtront ici.",
  "weak.practiceTop.one": "Entraînez-vous sur le concept principal ({n})",
  "weak.practiceTop.other": "Entraînez-vous sur les {n} premiers concepts",
  "weak.practiceThis": "S'entraîner là-dessus",
  "weak.seen.one": "vu {n} fois, la dernière le {date}",
  "weak.seen.other": "vu {n} fois, la dernière le {date}",
  "home.intro": "Les révisions programmées conservent ce que vous avez appris. Les comptes à faire suivent les mêmes règles qu'une session de révision.",
  "home.noCourses": "Aucun cours pour l'instant.",
  "home.createOnDashboard": "Créez-en un sur votre tableau de bord",
  "home.meta": "{due} à faire maintenant · {introduced} éléments sur {trained} introduits",
  "activity.pageIntro": "Réponses terminées par jour sur la dernière année, dans votre fuseau horaire. Une réponse compte dès qu'elle a une note finale, qu'elle soit juste ou fausse.",
  "reset.title": "Réinitialisez votre mot de passe",
  "reset.intro": "Saisissez l'e-mail de votre compte. Nous vous enverrons un lien pour choisir un nouveau mot de passe ; il fonctionne une seule fois, pendant 30 minutes.",
  "reset.send": "Envoyer le lien de réinitialisation",
  "reset.sentMessage": "S'il existe un compte avec cet e-mail, un lien pour réinitialiser son mot de passe est en route. Vérifiez votre boîte de réception (et les spams).",
  "reset.back": "Retour à la connexion",
  "reset.chooseTitle": "Choisissez un nouveau mot de passe",
  "reset.incomplete": "Ce lien est incomplet. Rouvrez le lien de l'e-mail ou demandez-en un nouveau.",
  "reset.done": "Votre mot de passe a été modifié. Tous les appareils ont été déconnectés : connectez-vous avec le nouveau mot de passe.",
  "reset.newPassword": "Nouveau mot de passe",
  "reset.repeat": "Répétez le nouveau mot de passe",
  "reset.tooShort": "Utilisez au moins {n} caractères.",
  "reset.mismatch": "Les deux mots de passe ne correspondent pas.",
  "reset.set": "Définir le nouveau mot de passe",
  "source.aria": "Passage source",
  "source.closeAria": "Fermer la source",
  "source.loading": "Chargement du passage…",
  "source.open": "Ouvrir le passage source",
  "course.loadingCurriculum": "Chargement du programme…",
  "mkt.addPlain": "Ajouter à mes cours",
  "sales.pageName": "Page du marketplace",
};
